<template>
  <div>
    <v-card
      rounded="lg"
      :loading="loading"
      :color="flat ? 'transparent' : undefined"
      :flat="flat"
      :class="{ 'md:fit-content!': !block }"
    >
      <div class="md:flex">
        <template v-if="!period">
          <MetricsPeriodSelect v-model="localPeriod" class="ma-2" />
          <div>
            <v-divider :vertical="!mobile" />
          </div>
        </template>

        <div
          class="flex flex-row gap-2 overflow-x-auto"
          :class="{ 'pa-2': !flat, 'md:pl-2': flat && !period }"
        >
          <template v-for="(item, i) in renderItems" :key="i">
            <div v-if="i && !flat" class="flex">
              <v-divider vertical />
            </div>
            <v-card
              variant="flat"
              rounded="lg"
              class="px-3 py-2"
              :class="{
                'info-card': flat,
                'min-w-[fit-content]!': !minItemWidth,
              }"
              :to="getUrl(item)"
              :min-width="minItemWidth"
            >
              <div class="heading-3">{{ item.title }}</div>
              <div class="flex justify-between gap-2">
                <div class="heading-1">{{ item.displayValue }}</div>
                <template v-if="item.hasSparkline">
                  <v-sparkline
                    v-if="item.sparkline"
                    class="min-w-150px"
                    :model-value="item.sparkline"
                    line-width="4"
                    color="primary"
                    auto-draw
                    smooth
                  />
                  <div
                    v-else
                    class="min-w-150px flex-grow flex items-end relative"
                  >
                    <div class="h-1.5px bg-primary w-full"></div>
                    <div
                      class="absolute bottom-[-5px] left-0 right-0 h-5px bg-indigo/20"
                    ></div>
                  </div>
                </template>
              </div>
            </v-card>
          </template>
        </div>

        <!-- TODO: implement for when metrics are overflowing -->
        <!-- <v-spacer />
        <div class="pa-2 flex-align">
          <v-btn elevation="1" rounded="e-lg" size="30" icon="chevron_left" />
          <v-btn elevation="1" rounded="s-lg" size="30" icon="chevron_right" />
        </div> -->
      </div>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useDisplay } from "vuetify";
import {
  MetricType,
  type GroupMetricsItem,
  type MetricsControls,
  type MetricsDataSource,
} from "../types";
import {
  addUnit,
  isDbMetricsDataSource,
  normaliseGenericDataSource,
} from "../utils";
import { get, isNil, sum } from "lodash";
import { useLocalStorage } from "@vueuse/core";

export default defineComponent({
  props: {
    metricsKey: { type: String, default: "default" },
    items: { type: Array as PropType<GroupMetricsItem[]>, required: true },
    flat: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    period: { type: String },
    minItemWidth: { type: [Number, String] },
  },
  setup(props) {
    const storeKey = `group-metrics:${props.metricsKey}`;
    const { mobile } = useDisplay();
    const localPeriod = useLocalStorage<string>(storeKey, "today");
    const until = now();

    const sourceKey = (index: number, handle: string) => {
      return `${index}/${handle}`;
    };

    const sources = computed(() => {
      const entries: MetricsDataSource<string>[] = [];

      props.items.forEach((item, i) => {
        const s = normaliseGenericDataSource(item.sources);
        for (const source of s) {
          if (isDbMetricsDataSource(source)) {
            entries.push({
              ...source,
              handle: sourceKey(i, source.handle!),
            });
          } else {
            entries.push(...s);
          }
        }
      });

      return entries;
    });

    const controls = computed((): MetricsControls => {
      const period = props.period || localPeriod.value;
      const since = match(period, {
        today: () => now().startOf("day"),
        year: () => now().startOf("year"),
        all: () => null,
        default: () => {
          const [unit, value] = period.split(":");
          return now()
            .subtract(parseInt(value), unit as any)
            .startOf("day");
        },
      });

      return {
        since: since ? formatToDatetime(since) : undefined,
        until: formatToDatetime(until),
      };
    });

    const mCtx = provideMetrics(sources, controls);

    const renderItems = computed(() => {
      return props.items.map((item, i) => {
        let sparkline: number[] | undefined;

        const valueSource = sourceKey(i, item.value);
        const result = mCtx.getResult(valueSource);
        let value: any;

        if (result.type == MetricType.VALUE) {
          value = get(result.datasets, [0, "value"]);
          // Handle 0 explicitly to avoid falsy check converting it to "—"
          if (isNil(value)) {
            value = "—";
          }
        } else {
          const data = get(result.datasets, [0, "data"], []);
          value = sum(data);
        }

        if (item.formatPrice && item.withUnit && result.unit) {
          value = isNil(value) ? "—" : formatPrice(value, result.unit.label);
        } else if (item.withUnit) {
          value = isNil(value) ? "—" : addUnit(value, result.unit);
        }

        if (item.trend) {
          const trendSource = sourceKey(i, item.trend);
          const result = mCtx.getResult(trendSource, MetricType.TREND);
          if (result) {
            const s = get(result.datasets, [0, "data"], []);
            if (sum(s) > 0) {
              sparkline = s;
            }
          }
        }

        return {
          ...item,
          hasSparkline: !!item.trend,
          displayValue: value,
          sparkline,
        };
      });
    });

    return {
      mobile,
      localPeriod,
      loading: mCtx.loading,
      renderItems,
    };
  },
  methods: {
    getUrl(item: any) {
      return "#";
    },
  },
});
</script>
